import { Box, Flex, IconButton, BoxProps, useToken } from '@everlywell/ui-kit';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { Mousewheel, FreeMode } from 'swiper/modules';
import { SwiperSlide, SwiperProps, SwiperClass } from 'swiper/react';

import { Swiper } from './Carousel.styles';

export interface CarouselProps extends SwiperProps {
  children: React.ReactNode[];
  arrowInsideOffset?: boolean;
  arrowPosition?: 'sides' | 'bottom' | 'top';
  insidePagination?: boolean;
  showPagination?: boolean;
  slideStyle?: React.CSSProperties;
  hasFadeOnEdges?: boolean;
  fadeBgColor?: BoxProps['backgroundColor'] & (string & {});
}

const Carousel: React.FC<CarouselProps> = (props) => {
  const {
    arrowInsideOffset,
    children,
    insidePagination,
    showPagination,
    arrowPosition = 'sides',
    hasFadeOnEdges = false,
    fadeBgColor = 'tints.white',
    ...rest
  } = props;

  const [swiper, setSwiper] = React.useState<SwiperClass | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [carouselFadeBgColor] = useToken('colors', [fadeBgColor]);

  const initializeSwiper = (swiper: SwiperClass) => {
    props.onSwiper?.(swiper);
    setSwiper(swiper);
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    props.onSlideChange?.(swiper);
    setIsBeginning(swiper?.isBeginning);
    setIsEnd(swiper?.isEnd);
  };

  const handleOnProgress = (swiper: SwiperClass) => {
    setIsBeginning(swiper?.isBeginning);
    setIsEnd(swiper?.isEnd);
  };

  const slides = children!.map((element, index) => (
    <SwiperSlide
      data-testid="swiper-slide"
      key={`swiper-slide-${index}`}
      style={props.slideStyle}
    >
      {element}
    </SwiperSlide>
  ));

  const totalBullets = swiper?.snapGrid?.length ?? slides.length;

  const insidePaginationWrapperStyles = {
    marginTop: 0,
  };

  return (
    <Box
      sx={
        hasFadeOnEdges
          ? {
              '&:after, &:before': {
                content: '""',
                position: 'absolute',
                width: '140px',
                height: `100%`,
                zIndex: 2,
                pointerEvents: 'none',
                transition: 'all 0.3s ease-in-out',
              },
              '&:before': {
                top: 0,
                left: 0,
                visibility: isBeginning ? 'hidden' : 'visible',
                opacity: isBeginning ? 0 : 1,
                background: `linear-gradient(270deg, rgba(255, 255, 255, 0.00) 10%, ${carouselFadeBgColor} 80%)`,
                transform: 'translateX(-25px)',
              },
              '&:after': {
                top: 0,
                right: 0,
                visibility: isEnd ? 'hidden' : 'visible',
                opacity: isEnd ? 0 : 1,
                background: `linear-gradient(90deg, rgba(255, 255, 255, 0.00) 10%, ${carouselFadeBgColor} 80%)`,
                transform: 'translateX(25px)',
              },
            }
          : {}
      }
    >
      <Swiper
        {...rest}
        data-testid="swiper-carousel"
        onSwiper={initializeSwiper}
        onSlideChange={handleSlideChange}
        onProgress={handleOnProgress}
        modules={[Mousewheel, FreeMode]}
      >
        {slides}
      </Swiper>

      {showPagination && totalBullets > 1 && (
        <Flex
          justifyContent={[
            'center',
            null,
            arrowPosition === 'sides' ? 'center' : 'space-between',
            arrowPosition === 'top' ? 'flex-start' : 'center',
          ]}
          alignItems="center"
          marginTop={6}
          py="1rem"
          sx={insidePagination ? insidePaginationWrapperStyles : {}}
        >
          <Box
            display={{
              base: 'none',
              largeTablet: 'flex',
            }}
            sx={{
              ...(arrowPosition === 'sides' && {
                position: 'absolute',
                top: 'calc(50% - 36px)',
                zIndex: 1,
                justifyContent: 'space-between',
                width: '100%',
                pointerEvents: 'none',
              }),
              ...(arrowPosition === 'top' && {
                position: 'absolute',
                top: '10rem',
                right: '4rem',
                zIndex: 1,
                justifyContent: 'flex-end',
                width: '100%',
                pointerEvents: 'none',
              }),
            }}
          >
            <IconButton
              icon={<CaretLeft />}
              aria-label="Previous slide"
              onClick={() => swiper?.slidePrev()}
              variant="secondary"
              sx={{
                mr: 2,
                pointerEvents: 'all',
                marginLeft: arrowInsideOffset ? 4 : 0,
              }}
              isDisabled={isBeginning}
              size="sm"
            />
            <IconButton
              icon={<CaretRight />}
              aria-label="Next slide"
              onClick={() => swiper?.slideNext()}
              variant="secondary"
              isDisabled={isEnd}
              sx={{
                pointerEvents: 'all',
                marginRight: arrowInsideOffset ? 4 : 0,
              }}
              size="sm"
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export default React.memo(Carousel);
